import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper } from '@mui/material';
import GlobalTableNoPagination from '../../../components/CustomTableWithPagination/GlobalTableNoPagination';
import { getOgdTeamColorById } from '../../../utils/constants/functions';
import { getOgdPointTracker } from '../../../redux/features/poinTrackerSlice';
import { hasTarget } from './OgdPointTrackerLanding';
import AssessmentDetailDialog from './AssessmentDetailDialog';
import AdmissionDetailDialog from './AdmissionDetailDialog';
import RevenueDetailDialog from './RevenueDetailDialog';
import SalesDetailDialog from './SalesDetailDialog';
import CalculationDialog from './CalculationDialog';

/**
 * OGD Point Tracker, the table.
 *
 * A SECOND, INDEPENDENT copy of the table under Team Wise Analytics. It shares no
 * endpoint and no slice entry with the one that is live today, so this one can be
 * rebuilt column by column without any risk to it.
 *
 * WHY COLUMNS SAY "N/A"
 *
 * Every column is either agreed with the database admin, in which case it shows
 * real numbers, or it is still being decided, in which case it shows N/A.
 *
 * The old table showed a number for every column whether or not anybody had agreed
 * what that number meant, so a placeholder zero looked exactly like a real zero.
 * Here an unfinished column is visibly unfinished.
 *
 * The backend keeps the same distinction: null means nobody has defined the column,
 * 0 means defined and the answer really is zero.
 */

/** A value nobody has agreed the meaning of yet. */
const notAgreed = (value) => value === null || value === undefined;

/** What an undefined column reads as. Not a zero, because it is not one. */
const NOT_AGREED = 'N/A';

const money = (value) =>
  notAgreed(value)
    ? NOT_AGREED
    : '₹ ' + Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const percent = (value) =>
  notAgreed(value) ? NOT_AGREED : Number(value).toFixed(2) + ' %';

const plain = (value) => (notAgreed(value) ? NOT_AGREED : String(value));

/**
 * Grey for a column nobody has agreed, normal for one that is done.
 *
 * It reads the RAW value off the row rather than the `value` it is handed, because
 * the table runs valueGetter first, so by this point `value` is already the string
 * "N/A" and no longer null. Reading the row keeps the two apart.
 */
const GREY = { color: '#9aa5b1', fontStyle: 'italic' };
const pendingStyle = (field) => ({ row }) => (notAgreed(row?.[field]) ? GREY : {});

/**
 * A number you can click to see what is behind it.
 *
 * Only when there is something to see: a zero, a blank or a not-agreed cell stays
 * plain text, so nobody clicks and gets an empty popup. Hand in no click handler
 * and the cell is plain text too, which is what the export gets.
 */
const clickableNumber = (field, onClick) => ({ row, value }) =>
  onClick && !notAgreed(row?.[field]) && Number(row[field]) > 0 ? (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onClick(row)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(row)}
      style={{
        cursor: 'pointer',
        color: '#2F72B3',
        fontWeight: 600,
        textDecoration: 'underline',
      }}
      title="See what is behind this number"
    >
      {value}
    </span>
  ) : (
    value
  );

/**
 * The columns.
 *
 * The click handlers are handed in rather than built here, because a column has no
 * business owning a dialog. Leave one out and that cell is plain text, which is
 * what the export and any other caller gets.
 *
 *   openDetail = { assessments: fn, admissions: fn, sales: fn, revenue: fn,
 *                   working: fn }
 *
 * `working` is the one for the calculated columns. It is handed the row with the
 * field name added, because one dialog explains all of them.
 */
/**
 * A worked-out number you can click to see HOW it was worked out.
 *
 * Unlike clickableNumber above this does not ask for a positive value. Pending is
 * a minus figure when the target has been beaten, and that is exactly the case
 * somebody wants explained. Only a not-agreed cell stays plain text.
 */
const clickableWorking = (field, onClick) => ({ row, value }) =>
  onClick && !notAgreed(row?.[field]) ? (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onClick({ ...row, field })}
      onKeyDown={(e) => e.key === 'Enter' && onClick({ ...row, field })}
      style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
      title="See how this was worked out"
    >
      {value}
    </span>
  ) : (
    value
  );

export const ogdPointTrackerHeader = (openDetail = {}) => {
  return [
    {
      field: 'rank',
      headerName: 'Rank',
      width: 110,
      headerTooltip:
        'Position in the list, worked out from the order the rows arrive in. Not ' +
        'stored.',
    },

    {
      field: 'oga',
      sortable: true,
      headerName: 'OGA',
      width: 200,
      headerTooltip:
        'user.name, exactly as stored. Nothing is trimmed or tidied, so a mismatch ' +
        'with the OGD sheet stays visible.',
    },

    {
      field: 'team',
      sortable: true,
      headerName: 'Team',
      width: 200,
      headerTooltip:
        'The squad they were in DURING the filter period, taken from employment ' +
        'history. Falls back to the squad they are in today where no history covers ' +
        'that period. Moved mid period, the later squad wins. Walked up: user, ' +
        'user_roles, usertype, designations, wing.',
      cellStyle: ({ row }) => {
        const { color = '#e5e7eb' } = getOgdTeamColorById(row?.wingId);
        return { color: color };
      },
    },

    {
      field: 'assessments',
      sortable: true,
      headerName: 'Assessments',
      width: 160,
      headerTooltip:
        'How many assessment reports they WROTE in the period. Counted from ' +
        'assessment_report by created_by and created_at. Every report counts, because ' +
        'the status column is 0 on every row so a finished one cannot be told from an ' +
        'abandoned one.',
      valueGetter: ({ value }) => plain(value),
      cellStyle: pendingStyle('assessments'),
      // click the number for the list of assessments behind it
      renderCell: clickableNumber('assessments', openDetail.assessments),
    },

    {
      field: 'admissions',
      sortable: true,
      headerName: 'Admissions',
      width: 150,
      headerTooltip:
        'Students they brought in, counted in the month THE FIRST REAL PAYMENT ' +
        'ARRIVED, not the month it was typed in. Hold payments are skipped, since a ' +
        'hold can be taken months before the admission. Nothing paid yet, the ' +
        'confirmation date is used. One student counts once. Voided enrolments are ' +
        'skipped, inactive students still count.',
      valueGetter: ({ value }) => plain(value),
      cellStyle: pendingStyle('admissions'),
      // click the number for the list of students behind it
      renderCell: clickableNumber('admissions', openDetail.admissions),
    },

    {
      field: 'sales',
      sortable: true,
      headerName: 'Sales',
      width: 150,
      headerTooltip:
        'The DEAL VALUE written that month, not the money collected. Tuition plus ' +
        'the admission fee, per course. One student buying three courses gives ' +
        'three sale lines but one admission, so this is never grouped by student. ' +
        'The tuition is the FIRST JOINING AMOUNT only: later session increases ' +
        'belong to the CRO, not the OGA. A Dirham admission fee is converted using ' +
        'the rate taken from the payments made for that student.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('sales'),
      // click the figure for the courses behind it
      renderCell: clickableNumber('sales', openDetail.sales),
    },

    {
      field: 'revenue',
      sortable: true,
      headerName: 'Revenue',
      width: 150,
      headerTooltip:
        'The money ACTUALLY RECEIVED that month. Sales is the deal value, this is ' +
        'the cash, and they differ because students pay in instalments. Credited to ' +
        'the OGA who owns the enrolment, but only when the OGA or Finance keyed the ' +
        'payment in. Client Relations collections are their own work rather than ' +
        'a sale by the OGA, so those are dropped. EMI processing fees are ' +
        'excluded, being a financing cost rather than money paid for teaching.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('revenue'),
      // click the figure for the payments behind it
      renderCell: clickableNumber('revenue', openDetail.revenue),
    },

    {
      field: 'revenueTarget',
      sortable: true,
      headerName: 'Revenue Target',
      width: 170,
      headerTooltip:
        'The monthly revenue target, from target_slot_fill. Set by the reporting ' +
        'head on the Targets and Slots screen. Read across the whole month, since ' +
        'the month is carried by created_on and has no column of its own.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('revenueTarget'),
    },

    {
      field: 'slotTarget',
      sortable: true,
      headerName: 'Slot',
      width: 110,
      headerTooltip:
        'The assessment target for the month, from target_slot_fill. Set beside ' +
        'the revenue target on the Targets and Slots screen.  SFR is measured ' +
        'against this number: SFR = Assessments / Slot x 100.  It sits here, next ' +
        'to Revenue Target, because the two are set together.',
      // a plain count, not money and not a percentage
      valueGetter: ({ value }) => plain(value),
      cellStyle: pendingStyle('slotTarget'),
    },

    {
      field: 'pending',
      sortable: true,
      headerName: 'Pending',
      width: 150,
      headerTooltip:
        'How much is still left to earn this month.  Pending = Revenue Target - ' +
        'Revenue.  Example: a target of 12,00,000 with 11,19,982 earned leaves ' +
        '80,018 pending, shown in red.  A minus figure means the target was ' +
        'beaten, shown in green.',
      valueGetter: ({ value }) => money(value),
      // Pending is what is STILL TO EARN, target minus revenue. Zero or below
      // means the target has been met or beaten, which is good. The old table had
      // this the other way round and painted a beaten target red.
      cellStyle: ({ row }) =>
        notAgreed(row?.pending)
          ? GREY
          : { color: row.pending <= 0 ? 'green' : 'red' },
      renderCell: clickableWorking('pending', openDetail.working),
    },

    {
      field: 'tar',
      sortable: true,
      headerName: 'TAR (%)',
      width: 150,
      headerTooltip:
        'Target Achievement Rate. How much of the target was actually earned.  ' +
        'TAR = Revenue / Revenue Target x 100.  Example: earned 17,51,140 ' +
        'against a target of 14,00,000 gives 125%.  100% means the target was ' +
        'met exactly, above 100% means it was beaten.',
      valueGetter: ({ value }) => percent(value),
      cellStyle: pendingStyle('tar'),
      renderCell: clickableWorking('tar', openDetail.working),
    },

    {
      field: 'arpu',
      sortable: true,
      headerName: 'ARPU',
      width: 150,
      headerTooltip:
        'Average Revenue Per Unit, meaning the average money received per ' +
        'student.  ARPU = Revenue / Admissions.  Example: 17,51,140 from 50 ' +
        'students gives 35,023 each.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('arpu'),
      renderCell: clickableWorking('arpu', openDetail.working),
    },

    {
      field: 'sfr',
      sortable: true,
      headerName: 'SFR (%)',
      width: 150,
      headerTooltip:
        'Slot Fill Rate. How much of the assessment target was actually done.  ' +
        'SFR = Assessments / Slot Target x 100.  The slot target is a separate ' +
        'number from the revenue target, set on the Targets and Slots screen.',
      valueGetter: ({ value }) => percent(value),
      cellStyle: pendingStyle('sfr'),
      renderCell: clickableWorking('sfr', openDetail.working),
    },

    {
      field: 'acr',
      sortable: true,
      headerName: 'ACR (%)',
      width: 150,
      headerTooltip:
        'How many assessments turned into admissions.  ACR = Admissions / ' +
        'Assessments x 100.  Example: 39 admissions from 137 assessments gives ' +
        '28%.  It can read over 100% where an admission was made without an ' +
        'assessment ever being recorded.',
      valueGetter: ({ value }) => percent(value),
      cellStyle: pendingStyle('acr'),
      renderCell: clickableWorking('acr', openDetail.working),
    },

    {
      field: 'incentive',
      sortable: true,
      headerName: 'Incentive',
      width: 150,
      headerTooltip:
        'Not agreed yet. Waiting on a definition from the database admin.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('incentive'),
    },

    {
      field: 'monthlyBonus',
      sortable: true,
      headerName: 'Monthly Bonus',
      width: 170,
      headerTooltip:
        'Not agreed yet. Waiting on a definition from the database admin.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('monthlyBonus'),
    },

    {
      field: 'dailyBonus',
      sortable: true,
      headerName: 'Daily Bonus',
      width: 160,
      headerTooltip:
        'Not agreed yet. Waiting on a definition from the database admin.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('dailyBonus'),
    },

    {
      field: 'total',
      sortable: true,
      headerName: 'Total',
      width: 150,
      headerTooltip:
        'Total = Incentive + Monthly Bonus + Daily Bonus.  Stays empty until all ' +
        'three are agreed.',
      valueGetter: ({ value }) => money(value),
      cellStyle: pendingStyle('total'),
    },
  ];
};

const OgdPointTracker = ({ fromDate, toDate, selectedWings, onlyWithTarget }) => {
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 100,
    search: '',
    sort: { field: '', direction: 'asc' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  const ogdPointTracker = useSelector((state) => state.pointTracker.ogdPointTracker);
  const { responseData, loading } = ogdPointTracker;
  const rows = responseData?.data?.rows || [];

  const dispatch = useDispatch();

  // whose assessments the popup is showing. null means it is closed. The whole row
  // is kept, not just the id, so the popup can show the name and check its own
  // count against the number that was clicked.
  const [assessmentsOf, setAssessmentsOf] = useState(null);
  const [admissionsOf, setAdmissionsOf] = useState(null);
  const [salesOf, setSalesOf] = useState(null);
  // a calculated column, carrying the row plus which one was clicked
  const [workingOf, setWorkingOf] = useState(null);
  const [revenueOf, setRevenueOf] = useState(null);

  // joined into one string so the memo only changes when the ticked squads really
  // change, not every time the parent hands down a freshly built array
  const wingIds = (selectedWings || []).join(',');

  const dispatchParams = useMemo(() => {
    return { fromDate, toDate, wingIds };
  }, [fromDate, toDate, wingIds]);

  const previousDispatchParams = useRef(dispatchParams);
  const isApiNeeded =
    JSON.stringify(previousDispatchParams.current) !== JSON.stringify(dispatchParams);

  useEffect(() => {
    if (Object.entries(ogdPointTracker?.responseData || {}).length === 0 || isApiNeeded) {
      dispatch(getOgdPointTracker(dispatchParams));
      previousDispatchParams.current = dispatchParams;
    }
  }, [dispatch, dispatchParams, ogdPointTracker?.responseData, isApiNeeded]);

  // hidden BEFORE the rank is worked out, on purpose. Ranking first and hiding
  // afterwards would leave a list reading 1, 4, 7, which looks like a broken
  // table rather than a filtered one.
  const visible = onlyWithTarget ? rows.filter(hasTarget) : rows;

  // rank is worked out here rather than stored, so it always follows the order the
  // rows arrive in and never goes stale against a re-sorted table
  const ranked = visible.map((row, index) => ({ ...row, rank: index + 1 }));

  return (
    /**
     * ZEBRA STRIPING, ONE ROW LIGHT AND THE NEXT TINTED.
     *
     * Written here rather than in the shared table, so only this screen changes
     * and every other table that uses GlobalTableNoPagination stays as it is.
     *
     * It is CSS, not a cellStyle. A cellStyle sets an inline background on the
     * cell, and an inline style beats the shared table's hover rule, which would
     * leave a row that never highlights under the cursor. A scoped stylesheet
     * keeps hover working, as long as the hover rule comes LAST here so it wins
     * against the stripe on equal specificity.
     */
    <Box
      mt={2}
      sx={{
        // the header: dark navy instead of the shared grey
        '& .global-table thead th': {
          backgroundColor: '#33415C',
          color: '#FFFFFF',
          borderRight: '1px solid rgba(255, 255, 255, 0.14)',
        },
        '& .global-table thead th:last-of-type': { borderRight: 'none' },
        // the sort arrows and the freeze pin sit ON that navy, so they have to
        // be light or they disappear into it
        '& .global-table thead th .sort-icons': { color: 'rgba(255,255,255,0.55)' },
        '& .global-table thead th .sort-icons .active': { color: '#FFFFFF' },
        '& .global-table thead th .freeze-btn': { color: '#FFFFFF', opacity: 0.5 },
        '& .global-table thead th .freeze-btn:hover': { opacity: 1 },

        // one row light, the next tinted
        '& .global-table tbody tr:nth-of-type(odd) > td': {
          backgroundColor: '#FFFFFF',
        },
        '& .global-table tbody tr:nth-of-type(even) > td': {
          backgroundColor: '#F5F8FC',
        },
        // last, so hovering still lifts the whole row above its stripe
        '& .global-table tbody tr:hover > td': {
          backgroundColor: '#E6F0FA',
        },
      }}
    >
      <Paper>
        <GlobalTableNoPagination
          rowUniqueId="rank"
          headers={ogdPointTrackerHeader({
            assessments: setAssessmentsOf,
            admissions: setAdmissionsOf,
            sales: setSalesOf,
            revenue: setRevenueOf,
            working: setWorkingOf,
          })}
          rows={ranked}
          tableState={tableState}
          setTableState={setTableState}
          loading={loading}
        />
      </Paper>

      <AssessmentDetailDialog
        open={Boolean(assessmentsOf)}
        onClose={() => setAssessmentsOf(null)}
        person={assessmentsOf}
        fromDate={fromDate}
        toDate={toDate}
      />

      <AdmissionDetailDialog
        open={Boolean(admissionsOf)}
        onClose={() => setAdmissionsOf(null)}
        person={admissionsOf}
        fromDate={fromDate}
        toDate={toDate}
      />

      <SalesDetailDialog
        open={Boolean(salesOf)}
        onClose={() => setSalesOf(null)}
        person={salesOf}
        fromDate={fromDate}
        toDate={toDate}
      />

      <RevenueDetailDialog
        open={Boolean(revenueOf)}
        onClose={() => setRevenueOf(null)}
        person={revenueOf}
        fromDate={fromDate}
        toDate={toDate}
      />

      <CalculationDialog
        open={Boolean(workingOf)}
        onClose={() => setWorkingOf(null)}
        person={workingOf}
        field={workingOf?.field}
      />
    </Box>
  );
};

export default OgdPointTracker;
